import { computed, ComputedRef } from 'vue'
import { useTimers } from '../store/timers'
import { storeToRefs } from 'pinia'

export type TimerDataLocationDeparting = {
  departing: string | undefined
  departingStoreName: string | undefined | null
  departingStoreNumber: number | undefined | null
}
export type TimerDataLocationDestination = {
  destination: string | undefined
  destinationStoreName: string | undefined | null
  destinationStoreNumber: number | undefined | null
}
export type UseMapLocationsTravelReturn = {
  /** Returns the departure in an object with {departure, departureStoreName, departureStoreNumber} */
  departing: ComputedRef<TimerDataLocationDeparting | undefined>
  /** Returns the destination in an object with {destination, destinationStoreName, destinationStoreNumber} */
  destination: ComputedRef<TimerDataLocationDestination | undefined>
}
/**
 * Maps the location values against the day's timers. Returns the departure location information and the destination information.
 * Takes an index paremeter for a default starting index, else it is the length of the days timers.
 * @param index index of the timer to computed values for.
 */
export const useMapLocationsTravel = (index?: number): UseMapLocationsTravelReturn => {
  const { getAllParentTimers } = storeToRefs(useTimers())
  //Set a default index
  const searchIndex = computed(() =>
    typeof index !== 'undefined' ? index : getAllParentTimers.value.length
  )

  const departing = computed(() => {
    const previousTimer = getAllParentTimers.value[searchIndex.value - 1]
    if (typeof previousTimer === 'undefined')
      return {
        departing: undefined,
        departingStoreName: undefined,
        departingStoreNumber: undefined,
        departingStoreId: undefined,
      }
    if (previousTimer.type === 'store') {
      return {
        departing: 'store',
        departingStoreName: previousTimer.data.store_name,
        departingStoreNumber: previousTimer.data.store_number,
        departingStoreId: previousTimer.data.store_id,
      }
    } else if (previousTimer.type === 'travel') {
      return {
        departing: previousTimer.data.destination,
        departingStoreName: previousTimer.data.destinationStoreName,
        departingStoreNumber: previousTimer.data.destinationStoreNumber,
        departingStoreId: previousTimer.data.destinationStoreId,
      }
    } else if (previousTimer.data.location !== 'store') {
      return {
        departing: previousTimer.data.location,
        departingStoreName: undefined,
        departingStoreNumber: undefined,
        departingStoreId: undefined,
      }
    } else if (previousTimer.data.location === 'store') {
      //go back until we find a travel destination but return {departing: undefined, departingStoreName: undefined, departingStoreNumber: undefined} if we don't find a store
      for (let pointer = searchIndex.value - 2; pointer > -1; pointer--) {
        const timer = getAllParentTimers.value[pointer]
        if (timer.type === 'store')
          return {
            departing: 'store',
            departingStoreName: timer.data.store_name,
            departingStoreNumber: timer.data.store_id,
            departingStoreId: timer.data.store_number,
          }
        if (timer.type === 'travel')
          return {
            departing: timer.data.destination,
            departingStoreName: timer.data.destinationStoreName,
            departingStoreNumber: timer.data.destinationStoreNumber,
            departingStoreId: timer.data.destinationStoreId,
          }
        else if (timer.data.location !== 'store')
          return {
            departing: undefined,
            departingStoreName: undefined,
            departingStoreNumber: undefined,
            departingStoreId: undefined,
          }
        else if (timer.data.location === 'store') continue
      }
      return {
        departing: undefined,
        departingStoreName: undefined,
        departingStoreNumber: undefined,
        departingStoreId: undefined,
      }
    }

    return { departing: undefined, departingStoreName: undefined, departingStoreNumber: undefined }
  })

  const destination = computed(() => {
    const nextTimer = getAllParentTimers.value[searchIndex.value + 1]
    if (typeof nextTimer === 'undefined')
      return {
        destination: undefined,
        destinationStoreName: undefined,
        destinationStoreNumber: undefined,
        destinationStoreId: undefined,
      }
    if (nextTimer.type === 'store') {
      return {
        destination: 'store',
        destinationStoreName: nextTimer.data.store_name,
        destinationStoreNumber: nextTimer.data.store_number,
        destinationStoreId: nextTimer.data.store_id,
      }
    } else if (nextTimer.type === 'travel') {
      return {
        destination: nextTimer.data.departing,
        destinationStoreName: nextTimer.data.departingStoreName,
        destinationStoreNumber: nextTimer.data.departingStoreNumber,
        destinationStoreId: nextTimer.data.departingStoreId,
      }
    } else if (nextTimer.data.location !== 'store') {
      return {
        destination: nextTimer.data.location,
        destinationStoreName: undefined,
        destinationStoreNumber: undefined,
        destinationStoreId: undefined,
      }
    } else if (nextTimer.data.location === 'store') {
      //go forward until we find a travel destination but returns undefined if we don't find a store
      for (
        let pointer = searchIndex.value + 2;
        pointer < getAllParentTimers.value.length;
        pointer++
      ) {
        const timer = getAllParentTimers.value[pointer]
        if (timer.type === 'store')
          return {
            destination: 'store',
            destinationStoreName: timer.data.store_name,
            destinationStoreNumber: timer.data.store_number,
            destinationStoreId: timer.data.store_id,
          }
        if (timer.type === 'travel') {
          return {
            destination: timer.data.departing,
            destinationStoreName: timer.data.departingStoreName,
            destinationStoreNumber: timer.data.departingStoreNumber,
            destinationStoreId: timer.data.departingStoreId,
          }
        } else if (timer.data.location !== 'store')
          return {
            destination: undefined,
            destinationStoreName: undefined,
            destinationStoreNumber: undefined,
            destinationStoreId: undefined,
          }
        else if (timer.data.location === 'store') continue
      }
      return {
        destination: undefined,
        destinationStoreName: undefined,
        destinationStoreNumber: undefined,
        destinationStoreId: undefined,
      }
    }

    return {
      destination: undefined,
      destinationStoreName: undefined,
      destinationStoreNumber: undefined,
      destinationStoreId: undefined,
    }
  })
  return {
    departing,
    destination,
  }
}
